import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Spinner from './Spinner';

const ViewAnalysis = ({ jobId }) => {
  const [truck, setTruck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchTruckData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/trucks/${jobId}`);
      setTruck(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Error fetching truck data:", error);
      setError(error.response?.data?.message || "Failed to fetch truck data");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchTruckData();
  }, [fetchTruckData]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!truck) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No truck data available</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">Truck Analysis</h2>
        {lastRefresh && (
          <div className="text-sm text-gray-500">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500">ID</h3>
            <p className="mt-1 text-lg text-gray-900">{truck.id}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Name</h3>
            <p className="mt-1 text-lg text-gray-900">{truck.name}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Status</h3>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              truck.status === 'completed' ? 'bg-green-100 text-green-800' :
              truck.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {truck.status}
            </span>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Distance</h3>
            <p className="mt-1 text-lg text-gray-900">
              {truck.monitorInfo?.distance ? 
                  `${(parseFloat(truck.monitorInfo.distance) * 0.000621371).toFixed(2)} miles` : 
                  'N/A'
              }
            </p>
          </div>
        </div>
        
        {truck.aqiData && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">AQI</h3>
              <p className="mt-1 text-lg text-gray-900">{truck.aqiData.aqi}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">PM2.5</h3>
              <p className="mt-1 text-lg text-gray-900">{truck.aqiData.pm25} µg/m³</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">NO₂</h3>
              <p className="mt-1 text-lg text-gray-900">{truck.aqiData.no2} ppb</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewAnalysis;
